import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  const isHome = pathname === '/';
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = isTransparent
    ? 'px-3 py-2 text-sm font-medium text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer'
    : 'px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer';

  const logoTextClass = isTransparent
    ? 'text-white'
    : 'text-foreground-900';

  const logoAccentClass = isTransparent
    ? 'text-primary-300'
    : 'text-primary-500';

  const hamburgerClass = isTransparent
    ? 'text-white'
    : 'text-foreground-700';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap shrink-0">
            <span className={`text-xl md:text-2xl font-heading font-semibold tracking-tight transition-colors duration-300 ${logoTextClass}`}>
              Basetao <span className={logoAccentClass}>Sheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            <Link to="/" className={linkClass}>
              <span className="flex items-center gap-1.5">
                <i className="ri-home-4-line text-base"></i>
                Home
              </span>
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={linkClass}
              >
                <span className="flex items-center gap-1.5">
                  <i className="ri-apps-line text-base"></i>
                  Browse Categories
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  <p className="px-4 py-1.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    All 14 Categories
                  </p>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-6 h-6 rounded object-cover flex-shrink-0"
                      />
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/article/basetao-spreadsheet-guide" className={linkClass}>
              <span className="flex items-center gap-1.5">
                <i className="ri-book-open-line text-base"></i>
                Shopping Guide
              </span>
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={linkClass}
            >
              <span className="flex items-center gap-1.5">
                <i className="ri-external-link-line text-base"></i>
                Basetao Spreadsheet
              </span>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-1 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isTransparent ? 'hover:bg-white/10' : 'hover:bg-background-100'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} ${hamburgerClass}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-3 max-h-[80vh] overflow-y-auto">
          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line text-lg text-primary-500"></i>
              Home
            </Link>
            <Link
              to="/article/basetao-spreadsheet-guide"
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line text-lg text-accent-600"></i>
              Shopping Guide
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-external-link-line"></i>
              Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-shopping-cart-line"></i>
              Buy It Now
            </a>
          </div>

          {/* Categories Accordion */}
          <div className="pt-2">
            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="flex items-center justify-between w-full px-2 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider"
            >
              <span>All Categories</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCategoriesOpen && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                    />
                    <span className="text-xs leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Article Links */}
          <div className="pt-2 border-t border-background-200/70">
            <p className="px-2 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Guides</p>
            <div className="grid grid-cols-2 gap-1">
              {articleLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-3 py-2 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

const articleLinks = [
  { to: '/article/basetao-spreadsheet-guide', label: 'Complete Guide' },
  { to: '/article/basetao-review', label: 'Basetao Review' },
  { to: '/article/is-basetao-legit', label: 'Is Basetao Legit?' },
  { to: '/article/basetao-shipping-cost', label: 'Shipping Cost' },
  { to: '/article/basetao-coupon-guide', label: 'Coupon Guide' },
  { to: '/article/basetao-warehouse-guide', label: 'Warehouse Guide' },
  { to: '/article/basetao-vs-superbuy', label: 'vs Superbuy' },
  { to: '/article/basetao-beginner-guide', label: 'Beginner Guide' },
  { to: '/article/basetao-spreadsheet-tutorial', label: 'Tutorial' },
  { to: '/article/basetao-alternatives', label: 'Alternatives' },
];